/**
 * FinancialReleaseCard — Sprint Feb 2026
 *
 * Toont Payment Status EN Release Status als twee onafhankelijke states op
 * ShipmentDetail. Alleen Manager/Admin/Owner kunnen handmatig vrijgeven
 * of terugzetten op hold — Medewerker ziet read-only.
 *
 * Architectuur:
 *  - payment_status blijft canonical uit sales_invoices/shipping_labels.
 *  - release_status wordt onafhankelijk gezet via
 *    PATCH /admin/shipping-labels/{id}/release-status.
 *  - reden verplicht bij manual actie; audit log automatisch.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { Lock, Unlock, ShieldCheck, TriangleAlert, Loader2 } from "lucide-react";
import { Button } from "../../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "../../ui/dialog";
import { Textarea } from "../../ui/textarea";
import { adminApi, useAuth, formatApiError } from "../../../context/AuthContext";

const PAYMENT_META = {
  paid:            { label: "BETAALD",            tone: "green" },
  waived:          { label: "KWIJTGESCHOLDEN",     tone: "green" },
  partial:         { label: "GEDEELTELIJK BETAALD", tone: "amber" },
  partially_paid:  { label: "GEDEELTELIJK BETAALD", tone: "amber" },
  unpaid:          { label: "OPENSTAAND",           tone: "red"   },
  not_paid:        { label: "OPENSTAAND",           tone: "red"   },
  payment_required:{ label: "BETALING VEREIST",     tone: "red"   },
  released:        { label: "VRIJGEGEVEN",          tone: "green" }, // legacy
};

const TONE_CLASSES = {
  green: "bg-emerald-50 border-emerald-200 text-emerald-900",
  amber: "bg-amber-50 border-amber-200 text-amber-900",
  red:   "bg-red-50 border-red-200 text-red-900",
  slate: "bg-slate-50 border-slate-200 text-slate-900",
};

function resolveEffectiveRelease(label) {
  const explicit = (label.release_status || "").toLowerCase();
  if (explicit === "released" || explicit === "hold") return explicit;
  const pay = (label.payment_status || "").toLowerCase();
  if (pay === "paid" || pay === "waived") return "released";
  return "hold";
}

export default function FinancialReleaseCard({ label, onChanged }) {
  const { hasPermission, user } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const canEdit = hasPermission("quotes:edit") && role !== "medewerker";
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTarget, setDialogTarget] = useState("hold"); // 'released' | 'hold'
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const pay = (label.payment_status || "unpaid").toLowerCase();
  const payMeta = PAYMENT_META[pay] || { label: pay.toUpperCase(), tone: "slate" };
  const rel = resolveEffectiveRelease(label);
  const relIsManual = (label.release_source || "").toLowerCase() === "manual";
  const relTone = rel === "released" ? (pay === "paid" || pay === "waived" ? "green" : "amber") : "red";

  const openDialog = (target) => {
    setDialogTarget(target);
    setReason("");
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!reason.trim() || reason.trim().length < 3) {
      toast.error("Geef een reden op (min. 3 karakters).");
      return;
    }
    setBusy(true);
    try {
      const resp = await adminApi.patch(
        `/admin/shipping-labels/${label.id}/release-status`,
        { release_status: dialogTarget, reason: reason.trim() },
      );
      toast.success(
        dialogTarget === "released"
          ? "Zending handmatig vrijgegeven voor transport."
          : "Zending op HOLD gezet — transport geblokkeerd.",
      );
      setDialogOpen(false);
      if (onChanged) onChanged(resp?.data);
    } catch (e) {
      toast.error(formatApiError(e, "Kon release-status niet wijzigen."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4"
         data-testid="financial-release-card">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-4 h-4 text-[#082c50]" />
        <div className="text-xs font-bold uppercase tracking-wide text-[#082c50]">
          Financieel & Vrijgave
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Payment status */}
        <div className={`border rounded-md p-3 ${TONE_CLASSES[payMeta.tone]}`}
             data-testid="payment-status-pill">
          <div className="text-[10px] font-bold uppercase tracking-wide opacity-70">
            Payment status
          </div>
          <div className="mt-1 text-base font-black tracking-tight">
            {payMeta.label}
          </div>
          {label.amount_due != null && (
            <div className="mt-0.5 text-[11px] opacity-80">
              Openstaand: EUR {Number(label.amount_due).toFixed(2)}
            </div>
          )}
        </div>

        {/* Release status */}
        <div className={`border rounded-md p-3 ${TONE_CLASSES[relTone]}`}
             data-testid="release-status-pill">
          <div className="text-[10px] font-bold uppercase tracking-wide opacity-70">
            Transportvrijgave
          </div>
          <div className="mt-1 text-base font-black tracking-tight flex items-center gap-1.5">
            {rel === "released" ? (
              <><Unlock className="w-4 h-4" /> RELEASED</>
            ) : (
              <><Lock className="w-4 h-4" /> HOLD</>
            )}
          </div>
          {relIsManual && (
            <div className="mt-0.5 text-[11px] opacity-80">
              Handmatig · door {label.released_by_email || "medewerker"}
            </div>
          )}
        </div>
      </div>

      {/* Actie-knoppen */}
      {canEdit ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {rel === "hold" && (
            <Button
              size="sm"
              onClick={() => openDialog("released")}
              className="rounded-none text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="release-status-btn-release"
            >
              <Unlock className="w-3.5 h-3.5 mr-1.5" /> Handmatig vrijgeven
            </Button>
          )}
          {rel === "released" && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => openDialog("hold")}
              className="rounded-none text-xs border-red-300 text-red-800 hover:bg-red-50"
              data-testid="release-status-btn-hold"
            >
              <Lock className="w-3.5 h-3.5 mr-1.5" /> Op HOLD zetten
            </Button>
          )}
        </div>
      ) : (
        <div className="mt-3 text-[11px] text-slate-500 italic flex items-center gap-1">
          <TriangleAlert className="w-3 h-3" />
          Alleen Manager, Admin of Owner kan vrijgave wijzigen.
        </div>
      )}

      {/* Reason dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="release-reason-dialog">
          <DialogHeader>
            <DialogTitle>
              {dialogTarget === "released"
                ? "Handmatig vrijgeven voor transport"
                : "Transport op HOLD zetten"}
            </DialogTitle>
            <DialogDescription>
              Deze actie wordt vastgelegd in het audit-log met uw naam en de reden.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs font-bold uppercase text-slate-600">
              Reden {" "}<span className="text-red-600">*</span>
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                dialogTarget === "released"
                  ? "Bv. 'Klant heeft telefonisch bevestigd dat betaling vandaag binnenkomt.'"
                  : "Bv. 'Betalingsafspraak niet nagekomen, terug op hold.'"
              }
              rows={4}
              data-testid="release-reason-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={busy}
              className="rounded-none text-xs"
            >
              Annuleren
            </Button>
            <Button
              onClick={submit}
              disabled={busy || reason.trim().length < 3}
              className={`rounded-none text-xs ${
                dialogTarget === "released"
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : "bg-red-600 hover:bg-red-700"
              } text-white`}
              data-testid="release-reason-confirm"
            >
              {busy && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              {dialogTarget === "released" ? "Vrijgeven" : "Op HOLD zetten"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
